import { jSith } from "../jquery-replacement";
import { S25Array } from "./s25.array";
import { S25Date } from "./s25.date";
import { S25ObjectUtil } from "./s25.object.util";
import { isArray, isDate, isDefined, isObject, isUndefined } from "./index";

export namespace S25Xml {
    export let X2JS_INSTANCE: any = null;
    export function GET_X2JS_INSTANCE(): any {
        if (X2JS_INSTANCE) {
            return X2JS_INSTANCE;
        } else {
            X2JS_INSTANCE = new window.X2JS({});
            return X2JS_INSTANCE;
        }
    }
    export function parseXml(str: string): any {
        str = str.replace(/\<([A-Za-z_]+)\s*([A-Za-z_\s":=])*\/>/gi, "<$1></$1>");
        return new window.DOMParser().parseFromString(str, "text/html").body.innerHTML;
    }

    export function parseSpaceResourcePreferences(prefStr: string): any {
        if (prefStr) {
            //expand self-closing tags as they cause the DOMParser to subsume the next element for some reason...
            let prefXml: string = parseXml(prefStr);
            //ANG-3625 &nbsp in the event text was preventing it from being parsed.
            prefXml = prefXml.replace(/&nbsp;/gi, "&#xA0;");
            if (prefXml) {
                return prettyConv(prefXml, null, {
                    reservation: true,
                    space_reservation: true,
                    resource_reservation: true,
                });
            }
            return {};
        }
        return {};
    }
    // ignore the double indent
    // https://code.google.com/p/x2js/
    export function json2xml_str(json: any): any {
        return GET_X2JS_INSTANCE().json2xml_str(json);
    }

    export function xml_str2json(xml: any): any {
        return GET_X2JS_INSTANCE().xml_str2json(xml);
    }

    export function find(element: any, name: string): any {
        // because of namespaces we use iterative match:
        // http://stackoverflow.com/questions/853740/jquery-xml-parsing-with-namespaces
        return jSith.find(element, "*").filter(function () {
            return this.nodeName === name;
        });
    }
    export function unescapeXml(str: string | number): string {
        str = jSith.toStr(str);
        return (
            (str &&
                str
                    .replace(/&amp;/g, "&")
                    .replace(/&lt;/g, "<")
                    .replace(/&gt;/g, ">")
                    .replace(/&quot;/g, '"')
                    .replace(/&apos;/g, "'")) ||
            str
        );
    }

    //https://stackoverflow.com/questions/7918868/how-to-escape-xml-entities-in-javascript
    export function escapeXml(str: string | number, doubleEscape?: boolean, skipList?: string[]): string {
        skipList = skipList || [];
        let resp: string = jSith.toStr(str);
        const replaceList: any[] = [
            { find: "&", replace: "&amp;" },
            { find: "&amp;nbsp;", replace: "&#xA0;" },
            { find: "nbsp;", replace: "&#xA0;" },
            { find: "<", replace: "&lt;" },
            { find: ">", replace: "&gt;" },
            { find: '"', replace: "&quot;" },
            { find: "'", replace: "&apos;" },
        ];

        jSith.forEach(skipList, (_: any, s: string) => {
            const idx = S25Array.findByProp(replaceList, "find", s);
            if (idx > -1) {
                replaceList.splice(idx, 1);
            }
        });

        jSith.forEach(replaceList, (_: any, r: any) => {
            resp = resp.replace(new RegExp(r.find, "ig"), r.replace);
        });

        return doubleEscape ? escapeXml(resp, false, skipList) : resp;
    }

    export function removeInvalidXmlCharacters(input: any) {
        if (isObject(input)) {
            S25ObjectUtil.dfs(input, (node: any, parentNode: any, childKey: any) => {
                if (typeof node === "string") {
                    try {
                        parentNode[childKey] = removeInvalidXmlCharacters(node);
                    } catch (error) {
                        // Skip read-only properties (like array indices)
                        // This can happen when trying to modify array elements or other read-only properties
                    }
                }
            });
        } else if (typeof input === "string") {
            // remove everything forbidden by XML 1.0 specifications, plus the unicode replacement character U+FFFD
            return input
                .replace(
                    /((?:[\0-\x08\x0B\f\x0E-\x1F\uFFFD\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))/g,
                    "",
                )
                .replace(/[\x00-\x09\x0B-\x0C\x0E-\x1F\x7F-\x9F]/g, "")
                .replace(/[\u0000-\u0008\u000E-\u001F\u007F-\u009F]/g, "");
        }
    }

    export function unicodeToChar(text: string, escape?: boolean): string {
        return text.replace(/\\u[A-F0-9]{1,4};/gi, function (match: string) {
            const resp: string = String.fromCharCode(parseInt(match.replace(/\\u|;/g, ""), 16));
            return escape ? escapeXml(resp) : resp;
        });
    }

    export function hexEntityToChar(text: string, escape?: boolean): string {
        return text.replace(/&#x[A-F0-9]{1,4};/gi, function (match: string) {
            const resp: string = String.fromCharCode(parseInt(match.replace(/[&#x;]/g, ""), 16));
            return escape ? escapeXml(resp) : resp;
        });
    }

    export function numericEntityToChar(text: string, escape?: boolean): string {
        return text.replace(/&#\d{1,4};/gi, function (match: string) {
            const resp: string = String.fromCharCode(parseInt(match.replace(/[&#;]/g, "")));
            return escape ? escapeXml(resp) : resp;
        });
    }

    export function json2Xml(json: JSON): string {
        //json2xml_str hex/numeric/unicode encodes many characters, which is not necessary for xml as they are totally valid
        //in xml. We undo that encoding with the *ToChar methods because Java xml->json does NOT render the characters, but keeps
        //the encoding as plain-text
        return unicodeToChar(hexEntityToChar(numericEntityToChar(json2xml_str(json), true), true), true);
    }
    export function uglifyJson(json: any, prefix?: string, attrMap?: any, skipMap?: any): any {
        const retJson: any = {};
        jSith.forEach(json, function (key: any, obj: any) {
            //if key starts with _, its an attribute so dont touch, same with if key contains :, or key obj/arr has __prefix child, or key obj is fun,
            //or key is in attr map, or key is in skip map. If none of those are true, then prefix key with prefix like "r25:{{key}}", else just use key
            let newKey =
                ((key.indexOf("_") !== 0 &&
                    key.indexOf(":") === -1 &&
                    (isUndefined(json[key]) ||
                        (isDefined(json[key]) &&
                            !json[key].__prefix &&
                            (isUndefined(json[key][0]) || !json[key][0].__prefix))) &&
                    typeof json[key] !== "function" &&
                    (!attrMap || !attrMap[key]) &&
                    (!skipMap || !skipMap[key]) &&
                    prefix) ||
                    "") + key;
            if (!skipMap || !skipMap[newKey]) {
                //skip if in skip map
                if (attrMap && attrMap[newKey]) {
                    //if in attr map add _, so that the xml transform will make it an attr
                    newKey = "_" + newKey;
                }
                retJson[newKey] = json[key];
                if (isArray(json[key])) {
                    for (let i = 0; i < json[key].length; i++) {
                        retJson[newKey][i] = uglifyJson(json[key][i], prefix, attrMap, skipMap);
                    }
                } else if (isDate(json[key])) {
                    retJson[newKey] = S25Date.toS25ISODateTimeStr(json[key]);
                } else if (isObject(json[key])) {
                    retJson[newKey] = uglifyJson(json[key], prefix, attrMap, skipMap);
                }
            }
        });
        return retJson;
    }

    export function s25Json2Xml(json: any, prefix?: string): string {
        prefix = jSith.coalesce(prefix, "r25:");
        return json2Xml(
            uglifyJson(
                json,
                prefix,
                { nil: "true", engine: true, pubdate: true, id: true, crc: true, status: true, href: true },
                { r25: true, xl: true, xsi: true },
            ),
        );
    }

    export function prettyConv<T = any>(xml: string, customKeyMap?: any, isArrayMap?: any): T {
        return prettifyJson(xml_str2json(xml), customKeyMap, isArrayMap);
    }

    export function prettifyJson(json: any, customKeyMap?: any, isArrayMap?: any) {
        //function to prettify xml to json conversions
        customKeyMap = customKeyMap || {};
        const ret: any = {};
        for (const key in json) {
            if (json.hasOwnProperty(key)) {
                let prettyKey = key;
                if (prettyKey.indexOf(":") > -1) {
                    prettyKey = prettyKey.substring(prettyKey.indexOf(":") + 1);
                }
                prettyKey = prettyKey.replace("__", "");
                if (prettyKey[0] === "_") {
                    prettyKey = prettyKey.substring(1);
                }
                prettyKey = customKeyMap[prettyKey] || prettyKey; //user can specify a map of xml2json keys to keys they prefer
                if (prettyKey !== "prefix") {
                    if (isArrayMap && isArrayMap[prettyKey] && !isArray(json[key])) {
                        json[key] = [json[key]];
                    }

                    if (isArray(json[key])) {
                        ret[prettyKey] = [];
                        for (let i = 0; i < json[key].length; i++) {
                            ret[prettyKey].push(prettifyJson(json[key][i], customKeyMap, isArrayMap));
                        }
                    } else if (json[key] && isObject(json[key])) {
                        json[key].__prefix = undefined;
                        if (!S25ObjectUtil.isEmptyBean(json[key])) {
                            if (json[key] && json[key].__text) {
                                ret[prettyKey] = json[key].__text;
                            } else {
                                ret[prettyKey] = prettifyJson(json[key], customKeyMap, isArrayMap);
                            }
                        }
                    } else {
                        ret[prettyKey] = json[key];
                    }
                }
            }
        }
        return ret;
    }
    export function s25CleanupHtml(text: string): string {
        //migrated from shared code in s25-resource-details/details.xsl
        return jSith
            .toStr(text)
            .replace(/\n/g, "<br/>")
            .replace(/.lt;/g, String.fromCharCode(60))
            .replace(/.gt;/g, String.fromCharCode(62))
            .trim();
    }
}
